import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  Trash2,
  AlertCircle,
  Inbox,
  ClipboardList,
  MessageSquare,
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { supabase } from '../../services/supabase';
import { useAuthStore } from '../../store/authStore';

// §128 · La campana del entrenador EN EL PANEL.
//
// El hueco que la mig 289 dejó escrito: el trigger escribe el aviso en
// `notifications` y manda el push, la app lo pinta desde §121… y en el panel no
// había campana. Medido antes de escribir esto: 12 avisos dirigidos a
// entrenadores, **los 12 sin leer y sin descartar** — 11 de chat (el más viejo
// del 8 de septiembre) y la asignación del 17. Ninguno alcanzable desde el
// panel, así que un entrenador que trabaja en el ordenador no los vio nunca.
//
// Por qué una página propia y no la de dirección (`NotificacionesPage`):
// aquélla vive DENTRO del bloque `AdminOnly` del router, que al monitor lo
// redirige a `/monitor/dashboard`. No es que se viera mal: no se llegaba.
//
// Criterio, copiado de la app a propósito (`NotificacionesScreen` §121/§123):
//   · La LISTA trae las propias **y las globales** (`user_id IS NULL`): un
//     comunicado de dirección va sin destinatario y la app se lo muestra al
//     entrenador. Si aquí no saliera, el móvil y el panel dirían cosas
//     distintas.
//   · El GLOBO cuenta solo las propias sin leer: una global no se puede marcar
//     leída —no hay `read` por usuario— y lo dejaría encendido para siempre.
//   · Y la tarjeta ABRE su destino. Solo para los dos tipos que tienen sitio en
//     el portal del entrenador; el resto se queda quieto.

// EL TOPE DE LA PÁGINA. Se piden TOPE + 1 filas y se pintan TOPE: si vuelve la
// de más, es que hay más, y la página lo DICE. Es la lección del círculo de
// Comunicaciones (#725): una lista que se acaba sola a los 100 no distingue «no
// hay más» de «no te lo estoy enseñando».
//
// Hoy aquí no corta a nadie —medido: el entrenador con más avisos tiene 9—, y
// eso es justo lo que lo hacía invisible. Esta página ya trae las descartadas,
// igual que la de dirección, donde el corte SÍ está pasando ya (7 personas, un
// admin y seis directores).
const TOPE = 100;

interface NotificationView {
  id: string;
  title: string;
  body: string;
  type: string | null;
  read: boolean;
  dismissed: boolean;
  created_at: string;
  user_id: string | null;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (isToday(d)) return `Hoy ${format(d, 'HH:mm')}`;
  if (isYesterday(d)) return `Ayer ${format(d, 'HH:mm')}`;
  return format(d, "d 'de' MMM 'de' yyyy HH:mm");
}

/**
 * A dónde lleva un aviso DENTRO del portal del entrenador.
 *
 * ⚠️ Solo destinos que existen aquí. La lección está escrita en
 * `NotificacionesScreen`: reusar una ruta del otro lado no da un error visible,
 * da un clic que no lleva a ninguna parte. `/monitor/evaluaciones` y
 * `/monitor/comunicaciones` son rutas del bloque `MonitorOnly`; `/evaluaciones`
 * y `/comunicaciones`, las de dirección, echarían al entrenador al login del
 * panel de admin.
 */
export function destinoDelAviso(type: string | null): { to: string; label: string } | null {
  if (type === 'evaluation') return { to: '/monitor/evaluaciones', label: 'Ver el informe' };
  if (type === 'chat') return { to: '/monitor/comunicaciones', label: 'Ver la conversación' };
  return null;
}

export default function MonitorNotificacionesPage() {
  const profile = useAuthStore((s) => s.profile);
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDismissed, setShowDismissed] = useState(false);
  const [error, setError] = useState('');
  // ⚠️ Una carga que falla NO es una bandeja vacía. Sin esto, un 403 pintaba el
  // error y, debajo, «no tienes avisos activos»: la frase tranquilizadora
  // encima de una lectura que no se pudo hacer.
  const [cargaFallida, setCargaFallida] = useState(false);
  // Vinieron más filas de las que caben. Ver `TOPE`.
  const [truncado, setTruncado] = useState(false);
  // Aparte del error de carga: si la lista se ve pero no se pudo marcar leída,
  // el entrenador tiene que saber por qué la campana sigue con globo.
  const [marcarFallo, setMarcarFallo] = useState('');

  const load = useCallback(async () => {
    if (!profile) return;
    const { data, error: qErr } = await supabase
      .from('notifications')
      .select('id, title, body, type, read, dismissed, created_at, user_id')
      .or(`user_id.eq.${profile.id},user_id.is.null`)
      .order('created_at', { ascending: false })
      .limit(TOPE + 1);
    if (qErr) {
      setError(qErr.message);
      setCargaFallida(true);
      // Una lectura que falló no afirma nada sobre el tope.
      setTruncado(false);
      setLoading(false);
      return;
    }
    setCargaFallida(false);
    // ⚠️ El tope se mide sobre la PÁGINA CRUDA, antes del filtro de descartadas
    // (`visible`): medirlo sobre lo ya filtrado diría «no hay más» justo cuando
    // el filtro se ha comido la prueba de que sí.
    const pagina = (data ?? []) as NotificationView[];
    setTruncado(pagina.length > TOPE);
    setItems(pagina.slice(0, TOPE));
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    void load();
  }, [load]);

  // Marca las PROPIAS como leídas al entrar (las globales no se leen: son de
  // todos). El error se cuenta, no se traga.
  useEffect(() => {
    if (!profile) return;
    void supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', profile.id)
      .eq('read', false)
      // Y NO TOCA LAS DESCARTADAS. Esta página pinta el histórico —el checkbox
      // «Mostrar descartadas»—, así que sin esto enseñaba un dato que su propia
      // visita destruía. Era la TERCERA copia de la misma línea, junto con
      // `NotificacionesPage` y `NotificacionesScreen` de la app; hoy ningún
      // entrenador tiene filas `dismissed AND NOT read` (medido: 0 en los 4),
      // así que aquí era latente, y precisamente por eso el primero que las
      // tuviera lo habría descubierto solo.
      .eq('dismissed', false)
      .then(({ error: updErr }) => {
        if (updErr) setMarcarFallo(updErr.message);
      });
  }, [profile]);

  async function handleDismiss(id: string) {
    const { error: rpcErr } = await supabase.rpc('dismiss_notification', { p_id: id });
    if (rpcErr) {
      setError(rpcErr.message);
      return;
    }
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, dismissed: true } : n)));
  }

  const visible = useMemo(
    () => (showDismissed ? items : items.filter((n) => !n.dismissed)),
    [items, showDismissed]
  );

  const unreadCount = useMemo(
    () => items.filter((n) => !n.read && !n.dismissed && n.user_id === profile?.id).length,
    [items, profile?.id]
  );

  return (
    <div className="space-y-6">
      <div className="card" data-testid="monitor-notifs-page">
        <div className="flex items-center gap-3 mb-6">
          <Bell size={20} className="text-gold-primary" />
          <h2 className="font-oswald text-2xl uppercase tracking-wider text-white">Mis avisos</h2>
          {unreadCount > 0 && (
            <span className="badge-gold ml-auto" data-testid="monitor-notifs-unread-count">
              {unreadCount} {unreadCount === 1 ? 'nuevo' : 'nuevos'}
            </span>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-error/10 border border-error/30 rounded-lg p-3 mb-4">
            <AlertCircle size={16} className="text-error flex-shrink-0" />
            <p className="text-error text-sm font-inter" data-testid="monitor-notifs-error">
              {error}
            </p>
          </div>
        )}

        {marcarFallo && (
          <p
            className="text-warning text-xs font-inter mb-4"
            data-testid="monitor-notifs-sin-marcar"
          >
            No se han podido marcar como leídos: seguirán saliendo como nuevos y la campana
            mantendrá el globo ({marcarFallo}).
          </p>
        )}

        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showDismissed}
              onChange={(e) => setShowDismissed(e.target.checked)}
              className="w-4 h-4 accent-gold-primary"
              data-testid="monitor-notifs-show-dismissed"
            />
            <span className="text-text-secondary text-sm font-inter">Mostrar descartados</span>
          </label>
          <button
            onClick={() => void load()}
            className="text-text-secondary hover:text-gold-primary text-sm font-inter"
            data-testid="monitor-notifs-refresh"
          >
            Refrescar
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
          </div>
        ) : cargaFallida ? null : visible.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-center py-12 text-text-secondary"
            data-testid="monitor-notifs-empty"
          >
            <Inbox size={32} className="mb-2 opacity-60" />
            <p className="font-inter text-sm">
              {showDismissed ? 'No tienes avisos' : 'No tienes avisos activos'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-bg-border" data-testid="monitor-notifs-list">
            {visible.map((n) => {
              const destino = destinoDelAviso(n.type);
              const Icono = n.type === 'evaluation' ? ClipboardList : MessageSquare;
              return (
                <li
                  key={n.id}
                  className={`py-4 flex items-start gap-3 ${n.dismissed ? 'opacity-50' : ''}`}
                  data-testid={`monitor-notif-item-${n.id}`}
                >
                  <div
                    className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${n.read || n.dismissed ? 'bg-bg-border' : 'bg-gold-primary'}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-white font-inter text-sm font-medium">{n.title}</p>
                      <p className="text-text-secondary text-xs whitespace-nowrap">
                        {formatWhen(n.created_at)}
                      </p>
                    </div>
                    <p className="text-text-secondary text-sm font-inter mt-1 whitespace-pre-wrap break-words">
                      {n.body}
                    </p>
                    {destino ? (
                      <button
                        onClick={() => navigate(destino.to)}
                        className="mt-2 inline-flex items-center gap-1.5 text-gold-primary hover:underline text-xs font-inter"
                        data-testid={`monitor-notif-open-${n.id}`}
                      >
                        <Icono size={13} /> {destino.label} ›
                      </button>
                    ) : (
                      n.type && (
                        <span className="inline-block mt-2 text-xs text-text-secondary opacity-70 uppercase tracking-wide">
                          {n.type}
                        </span>
                      )
                    )}
                  </div>
                  {n.dismissed ? (
                    <span
                      className="text-success text-xs flex items-center gap-1 mt-1 flex-shrink-0"
                      data-testid={`monitor-notif-dismissed-${n.id}`}
                    >
                      <Check size={14} /> Descartado
                    </span>
                  ) : (
                    <button
                      onClick={() => void handleDismiss(n.id)}
                      className="text-text-secondary hover:text-error transition-colors mt-1 flex-shrink-0"
                      title="Descartar"
                      data-testid={`monitor-notif-dismiss-${n.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {truncado && (
          <p
            className="mt-4 text-text-secondary text-xs font-inter text-center border border-bg-border rounded-lg p-3"
            data-testid="monitor-notifs-truncado"
          >
            Hay más de {TOPE} avisos y aquí solo caben los {TOPE} más recientes. Los anteriores no
            se han borrado: no están en esta página.
          </p>
        )}
      </div>
    </div>
  );
}
